import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../../components/ui/dialog';
import { RandomDotmSpinner } from '../../../../../components/ui/RandomDotmSpinner';
import { DocsLinkButton } from '@/features/help-feedback';
import type { SpatialColumnDeclaration } from '../../utils/spatialValueAccessor';
import {
  DeclareSpatialColumnFields,
  type SpatialDeclareFormValues,
} from './DeclareSpatialColumnFields';

export type { SpatialDeclareFormValues };

type DeclareSpatialColumnDialogProps = {
  open: boolean;
  schema: string;
  table: string;
  column: string;
  dataType?: string;
  siblingColumns: string[];
  /** Present → details/remove mode instead of the declare form. */
  existing?: SpatialColumnDeclaration;
  onConfirm: (values: SpatialDeclareFormValues) => void | Promise<void>;
  onRemove: () => void | Promise<void>;
  onCancel: () => void;
};

export function DeclareSpatialColumnDialog({
  open,
  schema,
  table,
  column,
  dataType,
  siblingColumns,
  existing,
  onConfirm,
  onRemove,
  onCancel,
}: DeclareSpatialColumnDialogProps) {
  const [removing, setRemoving] = useState(false);
  const target = `${schema}.${table}.${column}`;

  async function handleRemove() {
    setRemoving(true);
    try {
      await onRemove();
    } finally {
      setRemoving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="sm:max-w-md" data-testid="declare-spatial-dialog">
        <DialogHeader>
          <div className="flex items-center gap-1.5">
            <DialogTitle>{existing ? 'Coordinate column' : 'Declare as coordinates'}</DialogTitle>
            <DocsLinkButton url="https://jamsql.com/docs/spatial/" doc="spatial" />
          </div>
          <DialogDescription>
            <code className="font-mono text-xs" translate="no" data-testid="declare-spatial-target">
              {target}
            </code>{' '}
            {existing
              ? 'is declared as a coordinate column. It shows on the map even without native spatial support.'
              : 'has no native geometry type. Declare how it holds coordinates to view it on the map.'}
          </DialogDescription>
        </DialogHeader>

        {existing ? (
          <>
            <div className="space-y-1 text-xs" data-testid="declare-spatial-details">
              <p>
                Format: <span className="font-mono" translate="no">{existing.format}</span>
              </p>
              {existing.format === 'lat-lng-pair' && existing.lngColumn && (
                <p>
                  Longitude column:{' '}
                  <span className="font-mono" translate="no">{existing.lngColumn}</span>
                </p>
              )}
              {existing.format === 'latlng-text' && (
                <p>Axis order: {existing.axisOrder === 'lng-lat' ? 'lng, lat' : 'lat, lng'}</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onCancel} data-testid="declare-spatial-cancel">
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleRemove}
                disabled={removing}
                data-testid="declare-spatial-remove"
              >
                {removing ? (
                  <RandomDotmSpinner size={14} className="mr-2" />
                ) : (
                  <Trash2 className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
                )}
                Remove declaration
              </Button>
            </DialogFooter>
          </>
        ) : (
          <DeclareSpatialColumnFields
            dataType={dataType}
            siblingColumns={siblingColumns}
            onConfirm={onConfirm}
            onCancel={onCancel}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
